import { useEffect, useRef, useState, useCallback } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import { TargetingType } from '@/types/components/onboarding'

interface SampleTwin {
  name: string
  role: string
  segment: string
  image: string
  gradient: string
}

// --onboarding-portrait-deep corrects the dark-mode --primary-dark drift:
//   light: var(--platform-deep) = 177 53% 8% (unchanged, = old --primary-dark light)
//   dark:  var(--primary)       = 171 86% 40% (restores May 29 --primary-dark dark value)
// Use as Tailwind arbitrary stop: to-[hsl(var(--onboarding-portrait-deep))]

/** B2B-oriented synthetic profiles (business professionals) */
const B2B_SAMPLE_TWINS: Array<SampleTwin> = [
  {
    name: 'David Chen',
    role: 'VP Finance',
    segment: 'Mid-Market',
    image: 'https://storage.googleapis.com/fairgen-twins-profiles/persona-images/meticulous-elder-african-leader.png',
    gradient: 'from-primary via-primary to-[hsl(var(--onboarding-portrait-deep))]',
  },
  {
    name: 'Zoe Williams',
    role: 'Beauty Enthusiast',
    segment: 'Gen Z',
    image: 'https://storage.googleapis.com/fairgen-twins-profiles/persona-images/data-analyst-female.png',
    gradient: 'from-chart-secondary via-primary to-chart-accent',
  },
  {
    name: 'James Mitchell',
    role: 'Young Professional',
    segment: 'Retail Banking',
    image: 'https://storage.googleapis.com/fairgen-twins-profiles/persona-images/warm-smile-white-investor.png',
    gradient: 'from-chart-warning via-[hsl(var(--onboarding-portrait-deep))] to-primary',
  },
]

/** B2C-oriented synthetic profiles (lifestyle/consumer audiences) */
const B2C_SAMPLE_TWINS: Array<SampleTwin> = [
  {
    name: 'Julian Rodriguez',
    role: 'Lifestyle Enthusiast',
    segment: 'Wellness',
    image: 'https://storage.googleapis.com/fairgen-twins-profiles/persona-images/patient-western-european-coordinator.png',
    gradient: 'from-chart-secondary via-primary to-[hsl(var(--onboarding-portrait-deep))]',
  },
  {
    name: 'Jordan Kim',
    role: 'Home & Style',
    segment: 'In-Market',
    image: 'https://storage.googleapis.com/fairgen-twins-profiles/persona-images/dynamic-black-innovator.png',
    gradient: 'from-primary via-chart-accent to-[hsl(var(--onboarding-portrait-deep))]',
  },
  {
    name: 'Sam Taylor',
    role: 'Tech Intender',
    segment: 'Family',
    image: 'https://storage.googleapis.com/fairgen-twins-profiles/persona-images/contemporary-western-european-thinker.png',
    gradient: 'from-chart-warning via-[hsl(var(--onboarding-portrait-deep))] to-primary',
  },
]

/** Collect all remote image URLs for preloading */
export const ALL_TWIN_IMAGES = [...B2B_SAMPLE_TWINS, ...B2C_SAMPLE_TWINS]
  .map((t) => t.image)
  .filter((url) => url.startsWith('http'))

/** Preload images into browser cache via link[rel=preload] */
function usePreloadImages(urls: Array<string>) {
  const preloaded = useRef(false)
  useEffect(() => {
    if (preloaded.current) return
    preloaded.current = true
    urls.forEach((url) => {
      const link = document.createElement('link')
      link.rel = 'preload'
      link.as = 'image'
      link.href = url
      document.head.appendChild(link)
    })
  }, [urls])
}

/** Per-image loaded state for smooth reveal */
function useImageLoaded() {
  const [loaded, setLoaded] = useState(false)
  const onLoad = useCallback(() => setLoaded(true), [])
  return { loaded, onLoad }
}

interface TwinPortraitStackProps {
  targetingType?: TargetingType
  className?: string
}

export function TwinPortraitStack({ targetingType = 'B2B', className }: TwinPortraitStackProps) {
  const sampleTwins = targetingType === 'B2C' ? B2C_SAMPLE_TWINS : B2B_SAMPLE_TWINS
  const shouldReduceMotion = useReducedMotion()
  const motionDuration = shouldReduceMotion ? 0 : 0.5

  usePreloadImages(ALL_TWIN_IMAGES)

  return (
    <div className={cn('flex flex-col items-center', className)}>
      <motion.div
        className="flex items-center justify-center -space-x-8 md:-space-x-12"
        initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: motionDuration, ease: 'easeOut' }}
      >
        {sampleTwins.map((twin, idx) => {
          const isCenter = idx === 1

          return (
            <TwinCard
              key={twin.name}
              twin={twin}
              isCenter={isCenter}
              index={idx}
              shouldReduceMotion={!!shouldReduceMotion}
              motionDuration={motionDuration}
            />
          )
        })}
      </motion.div>
    </div>
  )
}

interface TwinCardProps {
  twin: SampleTwin
  isCenter: boolean
  index: number
  shouldReduceMotion: boolean
  motionDuration: number
}

function TwinCard({ twin, isCenter, index, shouldReduceMotion, motionDuration }: TwinCardProps) {
  const { loaded, onLoad } = useImageLoaded()

  return (
    <motion.div
      initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 50, scale: shouldReduceMotion ? 1 : 0.8 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        delay: shouldReduceMotion ? 0 : 0.3 + index * 0.15,
        duration: motionDuration,
        ease: 'easeOut',
      }}
      style={{ zIndex: isCenter ? 20 : 10 - Math.abs(index - 1) }}
      className={cn(
        'relative transition-transform duration-300 hover:-translate-y-2',
        isCenter ? 'w-44 md:w-56' : 'w-36 md:w-44'
      )}
    >
      <div
        className={cn(
          'relative overflow-hidden rounded-2xl bg-gradient-to-br shadow-elevated',
          twin.gradient,
        )}
      >
        <img
          src={twin.image}
          alt={twin.name}
          onLoad={onLoad}
          className={cn(
            'aspect-[3/4] w-full object-cover object-top transition-opacity duration-500 ease-out',
            loaded ? 'opacity-100' : 'opacity-0'
          )}
        />

        <div className="absolute inset-0 bg-gradient-to-t from-media/80 via-media/20 to-transparent" />

        <div className="absolute right-3 top-3">
          <Text
            as="span"
            size="xs"
            className="rounded-full bg-black/40 px-3 py-1 text-white shadow-sm backdrop-blur-md"
          >
            {twin.segment}
          </Text>
        </div>

        <div className="absolute bottom-0 left-0 right-0 flex flex-col gap-0.5 p-4">
          <Text
            as="span"
            size="sm"
            weight="medium"
            className="text-media-foreground"
          >
            {twin.name}
          </Text>
          <Text size="xs" className="text-media-foreground/80">
            {twin.role}
          </Text>
        </div>
      </div>
    </motion.div>
  )
}
